import { ServerError } from '@apollo/client';
import { isBefore } from 'date-fns';
import type { InferGetServerSidePropsType, NextPage } from 'next';

import { Article } from '#components/Article/Article';
import { LivestreamUpdateDocument } from '#components/Media/__generated__/MediaLivestream.generated';
import { dropUndefined } from '#cutils/misc/drop-undefined.ts';
import type { CacheTag } from '#cutils/page-context/edge-cache-tags';
import { createDefaultGetServerSideProps } from '#cutils/page-context/server-side-props';
import type { ArticleQuery } from '#domains/resolver/__generated__/resolve-article.generated';
import { resolveArticle } from '#domains/resolver/resolve-article';
import { envConfigServer } from '#sconfig/env.server';
import type { ArticleData } from '#shared/types/article';
import { getStaticMapUrlsForGeolocation, parseGeolocation } from '#sutils/google-maps/static-maps-url-generation';
import { logger } from '#utils/logging/logger';
import { extractSlugAndIdFromPathSegment } from '#utils/pathUtils';

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

const Page: NextPage<Props> = ({ article }: Props) => {
	return <Article article={article} />;
};

const BASE_URL_REGEXP = new RegExp(`^(?:${envConfigServer.BASE_URL})*(.*)`);

const coralInvalidationDate = envConfigServer.CORAL_TOKEN_INVALIDATE_BEFORE
	? new Date(envConfigServer.CORAL_TOKEN_INVALIDATE_BEFORE)
	: undefined;

/**
	Removes the leading baseUrl from the pathname
*/
function removeBaseUrl(pathname: string) {
	return pathname.replace(BASE_URL_REGEXP, '$1');
}

/**
 * Returns the category title for an article by following the rules:
 * 1. If the article is a breaking news article use a breaking news title
 * 2. Use the title of the article category
 * 3. Use an empty title (this happens only during preview when no category for the article is set)
 *
 * @param articleData
 */
function getArticleCategoryTitle(articleData: ArticleQuery['article']) {
	const breakingnewsEnd = articleData?.breakingnewsEnd;

	if (breakingnewsEnd && Date.now() <= new Date(breakingnewsEnd).getTime()) {
		return 'Eilmeldung';
	} else if (articleData?.categoryByPrimaryCategory?.title) {
		return articleData?.categoryByPrimaryCategory?.title;
	} else {
		return '';
	}
}

export const getServerSideProps = createDefaultGetServerSideProps(async (ctx, apolloClient, withCacheTags) => {
	try {
		const { id, slug } = extractSlugAndIdFromPathSegment(ctx.params?.article);

		const resolutionResult = await resolveArticle(apolloClient, id, slug, ctx.params?.category as string, withCacheTags, ctx.resolvedUrl);

		if ('notFound' in resolutionResult || 'redirect' in resolutionResult || 'depublished' in resolutionResult) {
			return resolutionResult;
		}

		const article = resolutionResult;

		const livestreamRowIds = article.modules.nodes
			.filter((module): module is NonNullable<typeof module> => module?.type === 'LIVESTREAM')
			.map(({ rowId }) => rowId);

		// populate apollo cache with livestream module data
		const result = await Promise.all(
			livestreamRowIds.map((rowId) => {
				return apolloClient.query({
					query: LivestreamUpdateDocument,
					variables: { rowId, withVideoOnDemand: true },
					fetchPolicy: 'cache-first',
				});
			})
		);

		for (const { error } of result) {
			if (ServerError.is(error)) {
				throw error;
			}
		}

		const navigationTitle = getArticleCategoryTitle(article);
		const cacheTags = new Set<CacheTag>();
		if (withCacheTags) {
			for (const tag of article.withCache ?? []) {
				cacheTags.add(tag as CacheTag);
			}
		}
		let canonicalUrl = article.shareUrl;

		if (canonicalUrl) {
			if (!canonicalUrl.includes(',')) {
				// legacy shareUrls might not have the id in the url
				canonicalUrl = `${canonicalUrl},${article.rowId}`;
			}

			const { pathname: canonicalPathname } = new URL(canonicalUrl, 'http://localhost');

			const pathnameWithoutBaseUrl = removeBaseUrl(canonicalPathname);
			const { pathname: originalPathname } = new URL(decodeURIComponent(ctx.resolvedUrl), 'http://localhost');

			if (pathnameWithoutBaseUrl !== originalPathname) {
				return {
					notFound: true,
				};
			}
		}

		const addCoralInvalidation = article.allowComments && !!coralInvalidationDate && isBefore(coralInvalidationDate, new Date());

		const geolocation = parseGeolocation(article.geolocation);
		let modifiedArticle: ArticleData;
		if (geolocation) {
			const staticMapUrls = getStaticMapUrlsForGeolocation(geolocation);

			modifiedArticle = { ...article, geolocation: { center: geolocation, staticMapUrls }, addCoralInvalidation };
		} else {
			modifiedArticle = { ...article, geolocation: undefined, addCoralInvalidation };
		}

		return {
			props: {
				article: dropUndefined(modifiedArticle),
				navigationTitle,
				cacheTags,
			},
		};
	} catch (error: any) {
		logger.error(error, `Error during article resolution`);
		throw error;
	}
});

export default Page;
